import { eventPhase } from "./event-dates";
import { splitPast } from "../events/event-past";
import type { MembershipCard } from "./schemas";

/** What the past and archived rules read off a membership card. */
export const cardTiming = (membership: MembershipCard) => ({
  startDate: membership.eventStartDate,
  endDate: membership.eventEndDate,
  timezone: membership.eventTimezone,
  archived: membership.eventArchived,
});

/**
 * My events, in the order the page shows them: the hero first, then the rest,
 * then the past.
 *
 * Past is decided on the event's own zone and by archiving (event-past.ts):
 * an event whose last day has gone by there, or an archived one, is never the
 * hero and does not sit among the rest. They come back newest first, cut at
 * the newest few unless `showAll`.
 *
 * The hero is the event that is running, else the soonest one that has not
 * started. Neither is one with no readable start date, since "next" is
 * meaningless for it. The rest run from soon to late, then the undated.
 * `Array.prototype.sort` is stable, so events that tie keep the order the
 * query returned them in.
 *
 * The day counting here is the reader's (`today`), as the cards' own labels
 * are. An event the reader's day calls over but its own zone does not (the
 * reader is ahead of it) is still running there, so it counts as live.
 */
export function arrangeMemberships(
  memberships: readonly MembershipCard[],
  today: string,
  now: Date,
  options: { showAll?: boolean; readerZone?: string } = {},
): {
  hero: MembershipCard | null;
  rest: MembershipCard[];
  past: MembershipCard[];
  pastTotal: number;
  hasMorePast: boolean;
} {
  const split = splitPast(memberships, cardTiming, now, options);

  const rows = split.current.map((membership) => {
    const kind = eventPhase(
      membership.eventStartDate,
      membership.eventEndDate,
      today,
    ).kind;
    return {
      membership,
      phase: kind === "ended" ? ("live" as const) : kind,
      start: membership.eventStartDate ?? "",
    };
  });

  // `yyyy-mm-dd` strings sort as dates, but only the ones that parsed are
  // ever compared: the unknown phase has no dates to compare.
  const byStart = (a: (typeof rows)[number], b: (typeof rows)[number]) =>
    a.start < b.start ? -1 : a.start > b.start ? 1 : 0;

  const live = rows.filter((r) => r.phase === "live").sort(byStart);
  const upcoming = rows.filter((r) => r.phase === "upcoming").sort(byStart);
  const undated = rows.filter((r) => r.phase === "unknown");

  const [first, ...others] = [...live, ...upcoming];
  return {
    hero: first?.membership ?? null,
    rest: [...others, ...undated].map((r) => r.membership),
    past: split.past,
    pastTotal: split.pastTotal,
    hasMorePast: split.hasMorePast,
  };
}
